<template>
  <v-main>
    <v-container fluid class="seller-dashboard">

      <!-- Header -->
      <v-row class="mb-2">
        <v-col cols="12">
          <div class="d-flex justify-space-between align-center">
            <div>
              <h1 class="dash-title">{{ getGreeting() }}</h1>
              <p class="dash-subtitle">{{ getCurrentDate() }}</p>
            </div>
            <v-btn color="primary" outlined small @click="refreshDashboard" :loading="isLoading">
              <v-icon left small>mdi-refresh</v-icon> Refresh
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Stat Cards -->
      <v-row dense class="mb-2">
        <v-col cols="6" sm="6" md="3" v-for="(stat, i) in sellerStats" :key="i">
          <v-card class="stat-card" elevation="1">
            <v-card-text class="pa-3">
              <div class="d-flex align-center">
                <div class="stat-icon-wrap mr-3" :class="'bg-' + stat.color">
                  <v-icon color="white">{{ stat.icon }}</v-icon>
                </div>
                <div>
                  <div class="stat-value">{{ stat.value }}</div>
                  <div class="stat-label">{{ stat.label }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Content -->
      <v-row class="mt-1">
        <!-- Left Column -->
        <v-col cols="12" md="8">

          <!-- Sales Overview -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="primary" small>mdi-chart-line</v-icon>
              Sales Overview
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="salesPeriod" mandatory dense class="toggle-sm">
                <v-btn x-small value="week">Week</v-btn>
                <v-btn x-small value="month">Month</v-btn>
                <v-btn x-small value="year">Year</v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-card-text class="pt-0">
              <canvas ref="salesChart" height="90"></canvas>
            </v-card-text>
          </v-card>

          <!-- Order Status -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="deep-orange" small>mdi-cart</v-icon>
              Order Status
            </v-card-title>
            <v-card-text class="pt-0">
              <div class="overview-cards-row mb-3">
                <div
                  class="overview-card"
                  v-for="(s, i) in orderStatuses"
                  :key="i"
                  @click="navigateTo('/orders?status=' + s.filter)"
                >
                  <div class="overview-card-dot" :style="{ background: s.color }"></div>
                  <div class="overview-card-count">{{ s.count }}</div>
                  <div class="overview-card-label">{{ s.label }}</div>
                </div>
              </div>
              <div class="chart-centered">
                <canvas ref="orderStatusChart" height="200"></canvas>
                <div v-if="allOrdersZero" class="no-data-overlay">
                  <v-icon color="grey lighten-2" size="40">mdi-cart-outline</v-icon>
                  <div class="no-data-text">No orders yet</div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Recent Orders -->
          <v-card class="mb-3 dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="indigo" small>mdi-format-list-bulleted</v-icon>
              Recent Orders
              <v-spacer></v-spacer>
              <v-btn text x-small color="primary" @click="navigateTo('/orders')">
                View All <v-icon right x-small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-0">
              <div v-if="recentOrders.length === 0" class="text-center py-6 grey--text">
                <v-icon large color="grey lighten-2" class="mb-2">mdi-cart-outline</v-icon>
                <div class="text-body-2">No orders yet</div>
              </div>
              <v-simple-table v-else dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Order No</th>
                      <th class="text-left">Customer</th>
                      <th class="text-left">Amount</th>
                      <th class="text-left">Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="recentOrders.length === 0">
                      <td colspan="5" class="text-center grey--text py-4">No orders yet</td>
                    </tr>
                    <tr v-for="order in recentOrders" :key="order.id">
                      <td class="primary--text font-weight-medium">{{ order.order_no }}</td>
                      <td>{{ order.customer }}</td>
                      <td class="font-weight-medium">₹{{ order.amount }}</td>
                      <td>
                        <status-chip x-small :color="getStatusColor(order.status)" :label="String(order.status)" />
                      </td>
                      <td>
                        <v-btn icon x-small @click="navigateTo('/orders/' + order.id)">
                          <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

        </v-col>


        <!-- Right Column -->
        <v-col cols="12" md="4">

          <!-- My Products Summary -->
          <v-card class="dash-card" elevation="1">
            <v-card-title class="dash-card-title">
              <v-icon left color="purple" small>mdi-package-variant</v-icon>
              My Products
              <v-spacer></v-spacer>
              <v-btn text x-small color="primary" @click="navigateTo('/seller-products')">
                View All <v-icon right x-small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-0">
              <div class="product-stat-row" v-for="(row, i) in productRows" :key="i">
                <div class="d-flex align-center mb-1">
                  <span class="prod-dot" :style="{ background: row.color }"></span>
                  <span class="prod-label">{{ row.label }}</span>
                  <span class="prod-val ml-auto">{{ row.value }}</span>
                </div>
                <v-progress-linear :value="row.pct" :color="row.color" height="4" rounded></v-progress-linear>
              </div>
            </v-card-text>
          </v-card>

        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import Chart from 'chart.js/auto';
import axios from 'axios';

export default {
  name: "SellerHomePage",
  data() {
    return {
      isLoading: false,
      salesPeriod: 'month',
      sellerStats: [
        { icon: 'mdi-currency-inr',  value: '₹0',  label: 'Total Revenue',   color: 'green'  },
        { icon: 'mdi-cart',           value: '0',   label: 'Total Orders',    color: 'blue'   },
        { icon: 'mdi-package-variant',value: '0',   label: 'My Products',     color: 'purple' },
        { icon: 'mdi-cash-clock',     value: '₹0',  label: 'Pending Payout',  color: 'orange' },
      ],
      orderStatuses: [
        { count: '0', label: 'Pending',   color: '#FF9800', filter: 'pending'   },
        { count: '0', label: 'Accepted',  color: '#2196F3', filter: 'accepted'  },
        { count: '0', label: 'Packed',    color: '#9C27B0', filter: 'packed'    },
        { count: '0', label: 'Shipped',   color: '#00BCD4', filter: 'shipped'   },
        { count: '0', label: 'Delivered', color: '#4CAF50', filter: 'delivered' },
        { count: '0', label: 'Cancelled', color: '#F44336', filter: 'cancelled' },
      ],
      recentOrders: [],
      payoutSummary: { totalEarnings: 0, settledAmount: 0, pendingPayout: 0, platformFees: 0 },
      productSummary: { total: 0, active: 0, inactive: 0 },
      charts: { sales: null, orderStatus: null },
    };
  },
  computed: {
    user()         { return this.$store.getters['auth/user']; },
    allOrdersZero() { return this.orderStatuses.every(s => s.count === '0'); },
    payoutRows() {
      return [
        { label: 'Total Earnings',  value: this.payoutSummary.totalEarnings,  cls: 'success--text'  },
        { label: 'Settled Amount',  value: this.payoutSummary.settledAmount,  cls: 'primary--text'  },
        { label: 'Pending Payout',  value: this.payoutSummary.pendingPayout,  cls: 'warning--text'  },
        { label: 'Platform Fees',   value: this.payoutSummary.platformFees,   cls: 'grey--text'     },
      ];
    },
    productRows() {
      const total = this.productSummary.total || 1;
      return [
        { label: 'Active',   value: this.productSummary.active,   pct: Math.round((this.productSummary.active   / total) * 100), color: '#4CAF50' },
        { label: 'Inactive', value: this.productSummary.inactive, pct: Math.round((this.productSummary.inactive / total) * 100), color: '#FF9800' },
        { label: 'Total',    value: this.productSummary.total,    pct: 100,                                                       color: '#2196F3' },
      ];
    },
  },
  mounted() {
    this.initializeDashboard();
  },
  beforeDestroy() {
    Object.values(this.charts).forEach(c => { if (c) c.destroy(); });
  },
  methods: {
    async initializeDashboard() {
      this.isLoading = true;
      try {
        await Promise.all([this.fetchSellerData(), this.loadCharts()]);
      } catch (e) {
        console.error('Seller dashboard init error:', e);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSellerData() {
      const calls = [
        axios.get('/orders/stats', { params: { period: 'all' } }),
        axios.get('/orders', { params: { page: 1, rowsPerPage: 5 } }),
        axios.get('/payouts/stats'),
        axios.get('/products/stats'),
      ];
      const [orderStats, orders, payouts, products] = await Promise.allSettled(calls);

      if (orderStats.status === 'fulfilled' && orderStats.value?.data?.data) {
        const d = orderStats.value.data.data;
        this.orderStatuses[0].count = (d.pending   || 0).toString();
        this.orderStatuses[1].count = (d.processing|| 0).toString();
        this.orderStatuses[2].count = (d.packed    || 0).toString();
        this.orderStatuses[3].count = (d.shipped   || 0).toString();
        this.orderStatuses[4].count = (d.delivered || 0).toString();
        this.orderStatuses[5].count = (d.cancelled || 0).toString();
        this.sellerStats[1].value   = (d.total     || 0).toString();
        if (this.charts.orderStatus) {
          this.charts.orderStatus.data.datasets[0].data = [
            d.pending || 0, d.processing || 0, d.packed || 0,
            d.shipped || 0, d.delivered  || 0, d.cancelled || 0,
          ];
          this.charts.orderStatus.update();
        }
      }

      if (orders.status === 'fulfilled' && orders.value?.data?.data) {
        const statusMap = { 0: 'Pending', 1: 'Processing', 2: 'Packed', 3: 'Shipped', 4: 'Shipped', 5: 'Shipped', 6: 'Delivered' };
        this.recentOrders = orders.value.data.data.slice(0, 5).map(o => {
          const customer = o.customer
            ? ((o.customer.first_name || '') + ' ' + (o.customer.last_name || '')).trim()
            : o.createdBy
              ? ((o.createdBy.first_name || '') + ' ' + (o.createdBy.last_name || '')).trim()
              : '—';
          return {
            id: o.id,
            order_no: o.order_number || ('ORD' + String(o.id).padStart(3, '0')),
            customer,
            amount: (o.total_amount || 0).toLocaleString('en-IN'),
            status: statusMap[o.order_status] || 'Pending',
          };
        });
      }

      if (payouts.status === 'fulfilled' && payouts.value?.data?.data) {
        const d = payouts.value.data.data;
        if (d.total_earnings  !== undefined) this.payoutSummary.totalEarnings  = d.total_earnings;
        if (d.settled_amount  !== undefined) this.payoutSummary.settledAmount  = d.settled_amount;
        if (d.pending_payout  !== undefined) {
          this.payoutSummary.pendingPayout = d.pending_payout;
          this.sellerStats[3].value = '₹' + this.formatNumber(d.pending_payout);
        }
        if (d.platform_fees   !== undefined) this.payoutSummary.platformFees   = d.platform_fees;
        if (d.total_revenue   !== undefined) this.sellerStats[0].value = '₹' + this.formatNumber(d.total_revenue);
      }

      if (products.status === 'fulfilled' && products.value?.data?.data) {
        const d = products.value.data.data;
        if (d.total_products  !== undefined) { this.productSummary.total   = d.total_products;  this.sellerStats[2].value = d.total_products.toString(); }
        if (d.active_products !== undefined)   this.productSummary.active   = d.active_products;
        if (d.inactive_products !== undefined) this.productSummary.inactive = d.inactive_products;
      }
    },

    loadCharts() {
      this.$nextTick(() => { this.initSalesChart(); this.initOrderStatusChart(); });
    },

    initSalesChart() {
      if (!this.$refs.salesChart) return;
      this.charts.sales = new Chart(this.$refs.salesChart.getContext('2d'), {
        type: 'line',
        data: {
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
          datasets: [
            { label: 'Sales (₹)', data: [0, 0, 0, 0], borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.08)', tension: 0.4, fill: true },
            { label: 'Orders',   data: [0, 0, 0, 0], borderColor: '#2196F3', backgroundColor: 'rgba(33,150,243,0.05)', tension: 0.4, fill: true, borderDash: [5, 5], yAxisID: 'y1' },
          ],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } } },
          scales: {
            y:  { beginAtZero: true, ticks: { callback: v => '₹' + v.toLocaleString(), font: { size: 10 } } },
            y1: { beginAtZero: true, position: 'right', grid: { drawOnChartArea: false }, ticks: { stepSize: 1, font: { size: 10 } } },
          },
        },
      });
    },

    initOrderStatusChart() {
      if (!this.$refs.orderStatusChart) return;
      this.charts.orderStatus = new Chart(this.$refs.orderStatusChart.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: this.orderStatuses.map(s => s.label),
          datasets: [{ data: [0, 0, 0, 0, 0, 0], backgroundColor: this.orderStatuses.map(s => s.color), borderWidth: 0 }],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
        },
      });
    },

    getGreeting() {
      const h = new Date().getHours();
      const name = this.user?.first_name || 'Seller';
      if (h < 12) return `Good Morning, ${name}!`;
      if (h < 17) return `Good Afternoon, ${name}!`;
      return `Good Evening, ${name}!`;
    },

    getCurrentDate() {
      return new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    },

    formatNumber(num) {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000)    return (num / 1000).toFixed(1) + 'K';
      return (num || 0).toString();
    },

    getStatusColor(status) {
      const map = { Pending: 'orange', Processing: 'blue', Packed: 'purple', Shipped: 'cyan darken-1', Delivered: 'success', Cancelled: 'error' };
      return map[status] || 'grey';
    },

    navigateTo(route) { this.$router.push(route).catch(() => {}); },

    async refreshDashboard() {
      this.isLoading = true;
      try { await this.fetchSellerData(); }
      catch (e) { console.error('Refresh error:', e); }
      finally { this.isLoading = false; }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.seller-dashboard { font-family: 'Inter', sans-serif; background: #f5f6fa; min-height: 100vh; padding: 12px 16px; }

/* Header */
.dash-title    { font-size: 1.8rem; font-weight: 700; color: #2c3e50; margin-bottom: 2px; }
.dash-subtitle { font-size: 0.95rem; color: #7f8c8d; margin: 0; }

/* Stat Cards */
.stat-card { border-radius: 12px !important; transition: transform 0.2s; }
.stat-card:hover { transform: translateY(-2px); }
.stat-icon-wrap {
  width: 44px; height: 44px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.stat-icon-wrap .v-icon { font-size: 22px !important; }
.bg-green  { background: #4CAF50; }
.bg-blue   { background: #2196F3; }
.bg-orange { background: #FF9800; }
.bg-purple { background: #9C27B0; }
.stat-value { font-size: 1.4rem; font-weight: 700; color: #2c3e50; line-height: 1.2; }
.stat-label { font-size: 0.75rem; color: #7f8c8d; font-weight: 500; }

/* Dash Cards */
.dash-card { border-radius: 12px !important; }
.dash-card-title { font-size: 0.95rem !important; font-weight: 600; padding: 12px 16px 8px !important; }
.dash-card > .v-card__text { padding: 4px 16px 16px !important; }

.toggle-sm .v-btn { font-size: 0.65rem !important; height: 24px !important; min-width: 50px !important; }

/* Order Status Overview */
.overview-cards-row { display: flex; gap: 8px; flex-wrap: wrap; }
.overview-card {
  flex: 1 1 0; min-width: 70px;
  background: #f8f9fa; border: 1px solid #eee; border-radius: 10px;
  padding: 10px 6px; text-align: center; cursor: pointer; transition: all 0.2s;
}
.overview-card:hover { background: #eef2ff; border-color: #c5cae9; transform: translateY(-1px); }
.overview-card-dot { width: 10px; height: 10px; border-radius: 50%; margin: 0 auto 5px; }
.overview-card-count { font-size: 1.3rem; font-weight: 700; color: #2c3e50; line-height: 1; margin-bottom: 2px; }
.overview-card-label { font-size: 0.68rem; color: #7f8c8d; font-weight: 500; }

.chart-centered { max-width: 260px; margin: 0 auto; min-height: 200px; position: relative; }
.no-data-overlay {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: #f9f9f9; border-radius: 50%; max-width: 200px; max-height: 200px;
  margin: auto; top: 0; left: 0; right: 0; bottom: 0;
}
.no-data-text { font-size: 0.8rem; color: #bdbdbd; margin-top: 6px; font-weight: 500; }

/* Payout Summary */
.payout-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
.payout-row:last-child { border-bottom: none; }
.payout-label { font-size: 0.85rem; color: #6c757d; font-weight: 500; }
.payout-value { font-size: 0.95rem; font-weight: 700; }

/* Product Summary */
.product-stat-row { margin-bottom: 12px; }
.product-stat-row:last-child { margin-bottom: 0; }
.prod-dot   { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 8px; flex-shrink: 0; }
.prod-label { font-size: 0.85rem; color: #2c3e50; font-weight: 500; }
.prod-val   { font-size: 0.85rem; font-weight: 700; color: #2c3e50; }

/* Recent Orders Table */
.v-simple-table th  { font-size: 0.78rem !important; color: #7f8c8d !important; font-weight: 600; }
.v-simple-table td  { font-size: 0.83rem !important; }

/* Responsive */
@media (max-width: 600px) {
  .dash-title       { font-size: 1.4rem; }
  .overview-cards-row { gap: 6px; }
  .overview-card    { min-width: 60px; padding: 8px 4px; }
  .overview-card-count { font-size: 1.1rem; }
  .chart-centered   { max-width: 200px; }
}

/* Dark theme */
.theme--dark .seller-dashboard { background: #121212; }
.theme--dark .dash-title, .theme--dark .stat-value, .theme--dark .overview-card-count,
.theme--dark .payout-value, .theme--dark .prod-label, .theme--dark .prod-val { color: #fff; }
.theme--dark .overview-card { background: #1e1e1e; border-color: #333; }
.theme--dark .payout-row { border-color: #333; }
</style>
